import React, { useState, useEffect } from "react";
import { Server, HardDrive, Activity } from "lucide-react";
import {
  fetchServicesPorts,
  fetchServicesDocker,
  fetchServicesSystemd,
} from "@/services/servicesService";

export default function ServicesTab() {
  const [ports, setPorts] = useState<any>(null);
  const [docker, setDocker] = useState<any>(null);
  const [systemd, setSystemd] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchServicesPorts().catch((e) => ({ error: String(e) })),
      fetchServicesDocker().catch((e) => ({ error: String(e) })),
      fetchServicesSystemd().catch((e) => ({ error: String(e) })),
    ]).then(([p, d, s]) => {
      if (cancelled) return;
      setPorts(p);
      setDocker(d);
      setSystemd(s);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-primary/20 bg-black/30 p-6">
        <p className="text-muted-foreground">Učitavanje...</p>
      </div>
    );
  }

  const portList = ports?.listen ?? ports ?? [];
  const containers = docker?.containers ?? [];
  const failedUnits = systemd?.failed ?? [];
  const keyServices = systemd?.key_services ?? {};

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-lg mb-3 flex items-center gap-2 text-foreground/80">
          <Activity className="h-4 w-4" />
          Listening Ports
        </h3>
        <div className="rounded-lg border border-primary/20 bg-black/30 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-primary/20">
                <th className="text-left p-2">Port</th>
                <th className="text-left p-2">Listen</th>
                <th className="text-left p-2">Process</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(portList) ? portList : []).slice(0, 30).map((p: any, i: number) => (
                <tr key={i} className="border-b border-primary/10">
                  <td className="p-2">{p.port ?? p.listen?.split(":")?.pop() ?? "-"}</td>
                  <td className="p-2 font-mono text-xs">{p.listen ?? "-"}</td>
                  <td className="p-2 text-muted-foreground text-xs">{p.process ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {portList.length === 0 && !ports?.error && (
            <p className="p-4 text-muted-foreground text-sm">Nema podataka</p>
          )}
          {ports?.error && (
            <p className="p-4 text-red-400 text-sm">Greška: {ports.error}</p>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-lg mb-3 flex items-center gap-2 text-foreground/80">
          <HardDrive className="h-4 w-4" />
          Docker Containers ({containers.length})
        </h3>
        <div className="rounded-lg border border-primary/20 bg-black/30 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-primary/20">
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Health</th>
              </tr>
            </thead>
            <tbody>
              {containers.slice(0, 25).map((c: any, i: number) => (
                <tr key={i} className="border-b border-primary/10">
                  <td className="p-2 font-mono">{c.name ?? c.Names ?? "-"}</td>
                  <td className="p-2">{c.status ?? c.Status ?? "-"}</td>
                  <td className="p-2">{c.health ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {containers.length === 0 && !docker?.error && (
            <p className="p-4 text-muted-foreground text-sm">Nema kontejnera</p>
          )}
          {docker?.error && (
            <p className="p-4 text-red-400 text-sm">Greška: {docker.error}</p>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-lg mb-3 flex items-center gap-2 text-foreground/80">
          <Server className="h-4 w-4" />
          Systemd
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-primary/20 bg-black/30 p-4">
            <h4 className="text-sm font-medium mb-2">Ključni servisi</h4>
            <ul className="space-y-1 text-sm">
              {Object.entries(keyServices).map(([name, status]) => (
                <li key={name} className="flex justify-between">
                  <span>{name}</span>
                  <span className={status === "active" ? "text-green-400" : "text-muted-foreground"}>
                    {status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-primary/20 bg-black/30 p-4">
            <h4 className="text-sm font-medium mb-2">Failed units ({failedUnits.length})</h4>
            {failedUnits.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nema failed</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {failedUnits.slice(0, 10).map((u: any, i: number) => (
                  <li key={i} className="text-red-400">{u.unit}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
